import useResponsiveSidebar from '../hooks/useResponsiveSidebar';
import { ResponsiveSidebar } from './ResponsiveSidebar';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDivisions } from '../utils/divisions';
import { CalendarDays, LogOut, Home as HomeIcon, Building2, Handshake, ClipboardList, PanelLeftClose, PanelLeftOpen, Briefcase, ClipboardCheck, Wrench } from 'lucide-react';
import { Button } from './ui/button';
import NotificationBell from './NotificationBell';

export default function FieldLayout({ children, title, dark }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const sidebar = useResponsiveSidebar('fieldSidebarCollapsed');
  const { collapsed, toggleCollapsed } = sidebar;

  const divisions = getDivisions(user);
  const multiDivision = divisions.length > 1 || user?.field_locked;
  const perms = user?.effective_permissions || [];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/field/calendar', icon: CalendarDays, label: 'Calendar', perm: 'field.calendar' },
    { path: '/field/work', icon: Briefcase, label: 'Field Work', perm: 'field.calendar' },
    ...(['management', 'field_management'].includes(user?.role) ? [{ path: '/field/approvals', icon: ClipboardCheck, label: 'Ticket Approvals', perm: 'field.calendar' }] : []),
    ...(['management', 'field_management'].includes(user?.role) ? [{ path: '/field/equipment', icon: Wrench, label: 'Equipment', perm: 'field.calendar' }] : []),
    { path: '/field/customers', icon: Building2, label: 'Customers', perm: 'customers' },
    { path: '/field/vendors', icon: Handshake, label: 'Vendors', perm: 'vendors' },
    { path: '/field/forms', icon: ClipboardList, label: 'Forms', perm: 'field.calendar' },
  ].filter((item) => perms.includes(item.perm));

  return (
    <div className={`flex min-h-screen ${dark ? 'bg-neutral-900' : 'bg-slate-50'}`} style={{ minHeight: '100dvh' }}>
      <ResponsiveSidebar sidebar={sidebar} testid="field-sidebar">
          <div className="p-6 border-b border-slate-800 shrink-0">
            <h1 className="text-2xl font-black tracking-tight">ShopManager<span className="text-sky-400">247</span></h1>
            <p className="text-xs text-sky-400/80 mt-1 uppercase tracking-wider">Field Operations</p>
          </div>

          <nav className="flex-1 p-4 space-y-2 overflow-y-auto" data-testid="field-sidebar-nav">
            {multiDivision && (
              <Link
                to="/home"
                data-testid="field-nav-home"
                className="flex items-center gap-3 px-4 py-3 rounded-md transition-colors text-slate-300 hover:bg-slate-800 hover:text-white"
              >
                <HomeIcon size={20} />
                <span className="font-medium">Home</span>
              </Link>
            )}
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.path || (item.path === '/field/work' && (location.pathname.startsWith('/field/work/') || location.pathname.startsWith('/field/tickets/')));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`field-nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                    active ? 'bg-sky-600 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <Icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div
            className="p-4 border-t border-slate-800 shrink-0"
            style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
          >
            <div className="px-4 py-2 mb-2">
              <p className="text-sm font-semibold text-white">{user?.name}</p>
              <p className="text-xs text-slate-400 uppercase tracking-wider">{user?.role?.replace(/_/g, ' ')}</p>
            </div>
            <Button
              onClick={handleLogout}
              data-testid="field-logout-button"
              variant="ghost"
              className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              <LogOut size={20} className="mr-3" />
              Logout
            </Button>
          </div>
      </ResponsiveSidebar>

      <main className="flex-1 overflow-auto min-w-0">
        <div className={`h-16 border-b flex items-center justify-between px-4 md:px-8 ${dark ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={toggleCollapsed}
              data-testid="field-sidebar-toggle"
              title={collapsed ? 'Show sidebar' : 'Hide sidebar'}
              className={`p-2 rounded-md transition-colors ${dark ? 'text-neutral-200 hover:bg-neutral-700' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            </button>
            <h2 className={`text-lg md:text-xl font-bold truncate ${dark ? 'text-neutral-100' : 'text-slate-900'}`}>{title || 'Field Calendar'}</h2>
          </div>
          <NotificationBell />
        </div>
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
